import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import MuiTooltip from "@mui/material/Tooltip";
import randomColor from "randomcolor";
import { useNavigate } from "react-router";
import {
  CellClickedEvent,
  ColDef,
  GetQuickFilterTextParams,
  GridApi,
  SideBarDef,
  ValueParserParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { toast } from "react-toastify";
import { useAuthStore, useCompanyStore } from "../../store";
import { Employee, EmployementStatus } from "../../types/Employee";

import {
  Box,
  HStack,
  IconCTAButton,
  VStack,
  isAdmin,
  isAdminViewer,
  isEsopViewer,
} from "../../components/utils";
import { usePermissionStore } from "../../store/permissionStore";
import { determineUserAccessToResource } from "../../utils/auth";
import { normalizeEmployeeTags } from "../../utils/employeePatch";
import {
  Action as DefaultAction,
  Resource,
} from "../../utils/interfaces/Companies";
import { useAuthorizationStore } from "../../store/useAuthorizationStore";
import Avatar from "../esopOverview/Avatar";
import { Action, CTADropdown } from "../../components/shared/Dropdown";

import {
  capitalizeFirstLetter,
  limitString,
  stringFormat,
} from "../../utils/string";
import QuickSearch from "../../components/shared/QuickSearch";
import SearchInput from "../../components/shared/SearchInput";
import { useGetFilterStates } from "../../queries/gridUtils";
import MyCustomFilters from "../../components/shared/MyCustomFilters";
import { saveTableFilterState } from "../../api/gridUtils";
import { AvailableTableFilters, SaveTableState } from "../../types/Table";
import StatusLabelColors from "../../constants/StatusLabelColors";
import { TableId } from "../../constants/TableIdConstants";
import { useTableStateManagement } from "../../components/shared/TableHook";
import CloudSetting from "../../components/shared/CloudIcon";
import AdvancedAgGridSearch from "../../components/AdvancedAgGridSearch";
import { useGetAssociatedUsers } from "../../queries/usersRoles";

function EmployeesTableAgGrid({
  handleAction,
  employeeTableData,
  onSelectedRows,
  gridRef,
  tableFilterCount,
  dateForLapsing,
}: {
  employeeTableData: Employee[];
  handleAction: (action: Action, employees: Employee[]) => void;
  onSelectedRows: (item: Employee[]) => void;
  gridRef: any;
  tableFilterCount: Function;
  dateForLapsing: string;
}) {
  const { permission } = usePermissionStore();
  const { companyData } = useCompanyStore();
  const excludeWithdrawnFromTotal = Boolean(
    companyData?.companySettings?.grantLetterSettings?.excludeWithdrawnFromTotal
  );
  const isUserAdmin = isAdmin();
  const hasDeletePermission = determineUserAccessToResource(
    permission?.aclList || [],
    Resource.Employee,
    DefaultAction.Delete
  );

  const { data: associatedUsers } = useGetAssociatedUsers();
  const managerUserIdToName = useMemo(() => {
    const map = new Map<string, string>();
    (associatedUsers || [])
      .filter((user) => user.role?.name === "MANAGER")
      .forEach((manager) => {
        if (manager.userId != null)
          map.set(manager.userId.toString(), manager.name);
      });
    return map;
  }, [associatedUsers]);

  employeeTableData.sort(
    (a, b) =>
      new Date(b.dateOfJoin).getTime() - new Date(a.dateOfJoin).getTime()
  );

  const gridApi = useRef<GridApi | any>(null);
  // route params logic
  const navigate = useNavigate();

  const componentsRegistery = useMemo(
    () => ({
      employeeInfo: memo(EmployeeInfoRender),
      vestedFactor: memo(EmployeeVestedFactor),
    }),
    []
  );

  const sideBar = useMemo<SideBarDef>(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          minWidth: 225,
          width: 225,
          maxWidth: 225,
          toolPanelParams: {
            suppressRowGroups: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressValues: true,
          },
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
          minWidth: 180,
          maxWidth: 400,
          width: 250,
        },
      ],
    }),
    []
  );
  const filterparams = {
    filterParams: {
      filterOptions: [
        "lessThan",
        {
          displayKey: "lessThanWithNulls",
          displayName: "Less Than with Nulls",
          predicate: ([filterValue]: any, cellValue: number | null) =>
            cellValue == null || cellValue < filterValue,
        },
        "greaterThan",
        {
          displayKey: "greaterThanWithNulls",
          displayName: "Greater Than with Nulls",
          predicate: ([filterValue]: any, cellValue: number | null) =>
            cellValue == null || cellValue > filterValue,
        },
        {
          displayKey: "betweenExclusive",
          displayName: "Between (Exclusive)",
          predicate: ([fv1, fv2]: any, cellValue: number | null) =>
            cellValue == null ||
            // eslint-disable-next-line no-mixed-operators
            (fv1 < cellValue && fv2 > cellValue),
          numberOfInputs: 2,
        },
      ],
    },
  };
  const { data, refetch } = useGetFilterStates("EMPLOYEES");

  const handleCellClick = (cellParams: CellClickedEvent<any, any>) => {
    if (cellParams.column.getColId() !== "action-column") {
      const rowData = employeeTableData.find(
        (row) => row.id === cellParams?.data?.id
      );
      if (rowData) {
        navigate(`/options/employeeDetails/${rowData.id}`);
      }
    }
  };

  const saveNewFilter = (filterName: string) => {
    const filterState = gridApi.current?.api.getFilterModel();
    const filterStateToSave: SaveTableState = {
      filterName,
      filterState,
      tableName: "EMPLOYEES",
    };
    saveTableFilterState(filterStateToSave).then((_) => {
      toast(`${filterName} saved successfully`, {
        autoClose: 2000,
        type: "success",
      });
      refetch();
    });
  };
  const onFilterSelected = (filterItem: AvailableTableFilters | null) => {
    gridApi?.current?.api.setFilterModel(filterItem?.filterState);
  };
  function numberValueParser(params: ValueParserParams) {
    const res = Number.parseInt(params.newValue, 10);
    if (Number.isNaN(res)) {
      return undefined;
    }
    return res;
  }
  const TagsCell = (params: any) => {
    const tags = normalizeEmployeeTags(params.value);
    if (!tags.length) return null;
    return (
      <div className="flex flex-wrap gap-1">
        {tags.map((tag) => (
          <span
            key={tag.value}
            className="inline-block px-2 py-1 text-xs bg-gray-200 rounded"
          >
            {tag.label}
          </span>
        ))}
      </div>
    );
  };
  const ManagerAvatar = ({ name }: { name: string }) => {
    const backgroundColor = randomColor({
      luminosity: "dark",
      format: "rgba",
      alpha: 0.2,
      seed: name,
    });
    const color = randomColor({
      luminosity: "dark",
      format: "rgba",
      alpha: 1,
      seed: name,
    });
    return (
      <div
        style={{ backgroundColor, color }}
        className="flex items-center justify-center w-6 h-6 rounded-full border-2 border-white text-[11px] font-medium -ml-2 first:ml-0"
      >
        {name ? name.charAt(0).toUpperCase() : ""}
      </div>
    );
  };
  const ManagersCell = (params: any) => {
    const names: string[] = params.value || [];
    if (!names.length) return null;
    const visible = names.slice(0, 3);
    const remaining = names.length - visible.length;
    return (
      <MuiTooltip title={names.join(", ")} arrow>
        <div className="flex items-center h-full cursor-default">
          {visible.map((name, index) => (
            <ManagerAvatar key={`${name}-${index}`} name={name} />
          ))}
          {remaining > 0 && (
            <div className="flex items-center justify-center w-6 h-6 rounded-full border-2 border-white bg-gray-200 text-gray-600 text-[10px] font-medium -ml-2">
              +{remaining}
            </div>
          )}
        </div>
      </MuiTooltip>
    );
  };
  const isValidDate = (date: string) =>
    Number.isNaN(date) &&
    new Date(date).toString() !== "Invalid Date" &&
    !Number.isNaN(Date.parse(date));
  const normaliseValue = (value: string | number) => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      // check if it can be converted to number first
      // assume if commas are there
      if (isValidDate(value)) return new Date(value).valueOf();
      try {
        value = value.replaceAll(",", "");
      } catch (TypeError) {
        value = value.replace(/,./g, "");
      }
      return parseFloat(value);
    }
    return value;
  };
  const sortComparator = (
    valueA: number | string,
    valueB: number | string,
    ..._: any[]
  ) => normaliseValue(valueA) - normaliseValue(valueB);
  const stringToNumberConvertor = (formattedNumber: string) =>
    parseInt(formattedNumber.toString().replaceAll(",", ""), 10);

  const handleSelectionChanged = (item: any) => {
    const selectedRows = item.api.getSelectedRows();
    onSelectedRows(selectedRows);
  };
  const {
    filterCondition,
    setFilterCondition,
    isSaving,
    uploadFilter,
    uploadColumn,
    tableState,
  } = useTableStateManagement(gridApi, TableId.employeeTable);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      wrapText: true,
      flex: 1,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: "multiline text-xs font-medium text-gray-dark leading-5",
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      comparator: sortComparator,
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
    }),
    []
  );
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "EMPLOYEE NAME",
        checkboxSelection: true,
        width: 200,
        autoHeight: true,
        cellStyle: { paddingTop: "15px", lineHeight: "20px" },
        wrapText: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        field: "employeeInfo",
        filter: "agMultiColumnFilter",

        getQuickFilterText: (params) => {
          const {
            employeeName,
            employeeDesignation,
            department,
            employmentStatus,
            grade,
          } = params?.value || {};

          const valuesToPass = [
            employeeName,
            employeeDesignation,
            department,
            employmentStatus,
            grade,
          ].filter((value) => value !== undefined && value !== null);
          return valuesToPass.toString();
        },

        initialWidth: 200,
        minWidth: 200,
        sortable: true,
        filterParams: {
          maxNumConditions: 5,
          buttons: ["reset"],
          filters: [
            {
              title: "Employee Name",
              display: "subMenu",
              filter: "agSetColumnFilter",
              buttons: ["reset"],
              filterParams: {
                buttons: ["reset"],
                suppressSelectAll: false,
                suppressMiniFilter: false,
                keyCreator: (params: any) => {
                  const employeeName = params.value.employeeName;
                  return employeeName;
                },
                valueFormatter: (params: any) => {
                  const employeeName = params.value.employeeName;
                  return employeeName;
                },
              },
            },
            {
              title: "Subsidiary Name/Holding Name",
              filter: "agSetColumnFilter",
              display: "subMenu",
              buttons: ["reset"],
              filterParams: {
                buttons: ["reset"],
                suppressSelectAll: false,
                suppressMiniFilter: false,
                comparator: sortComparator,
                keyCreator: (params: any) => {
                  const companyName = params.value.companyName;
                  return companyName && companyName !== ""
                    ? companyName
                    : "(Blanks)";
                },
                valueFormatter: (params: any) => {
                  const companyName = params.value.companyName;
                  return companyName && companyName !== ""
                    ? companyName
                    : "(Blanks)";
                },
              },
            },
            {
              title: "Designation",
              filter: "agSetColumnFilter",
              display: "subMenu",
              buttons: ["reset"],
              filterParams: {
                buttons: ["reset"],
                suppressSelectAll: false,
                suppressMiniFilter: false,
                comparator: sortComparator,

                keyCreator: (params: any) => {
                  const employeeDesignation = params.value.employeeDesignation;
                  return employeeDesignation;
                },
                valueFormatter: (params: any) => {
                  const employeeDesignation = params.value.employeeDesignation;

                  return employeeDesignation;
                },
              },
            },
            {
              title: "Department",
              filter: "agSetColumnFilter",
              display: "subMenu",
              buttons: ["reset"],
              filterParams: {
                buttons: ["reset"],
                suppressSelectAll: false,
                suppressMiniFilter: false,
                comparator: sortComparator,

                keyCreator: (params: any) => {
                  const department = params.value.department;
                  return department;
                },
                valueFormatter: (params: any) => {
                  const department = params.value.department;

                  return department;
                },
              },
            },
            {
              title: "Grade",
              filter: "agSetColumnFilter",
              display: "subMenu",
              buttons: ["reset"],
              filterParams: {
                buttons: ["reset"],
                suppressSelectAll: false,
                suppressMiniFilter: false,
                comparator: sortComparator,

                keyCreator: (params: any) => {
                  const grade = params.value.grade;
                  return grade && grade !== "" ? grade : "(Blanks)";
                },
                valueFormatter: (params: any) => {
                  const grade = params.value.grade;
                  return grade && grade !== "" ? grade : "(Blanks)";
                },
              },
            },
          ],
        },
        comparator(valueA, valueB, ..._other) {
          return valueA.employeeName
            ?.toLowerCase()
            ?.localeCompare(valueB.employeeName?.toLowerCase());
        },
        menuTabs: ["filterMenuTab"],
        cellRendererParams: ({ value }: { value: any }) => value.props,
        cellRenderer: "employeeInfo",
      },

      {
        headerName: "EMPLOYMENT STATUS",
        minWidth: 175,
        filter: "agSetColumnFilter",
        field: "employmentStatus",
        cellStyle: { paddingTop: "15px", lineHeight: "0" },
        comparator(valueA, valueB, ..._other) {
          return valueA?.props?.employmentStatus
            ?.toLowerCase()
            ?.localeCompare(valueB?.props?.employmentStatus?.toLowerCase());
        },
        filterParams: {
          buttons: ["reset"],
          suppressSelectAll: false,
          suppressMiniFilter: false,
        },
        filterValueGetter: (params) => {
          const props = params?.getValue("employmentStatus")?.props;
          if (props?.isPendingResignation) return "PENDING RESIGNATION";
          if (props?.isResignationSchedule) return "RESIGNATION SCHEDULED";
          return props?.employmentStatus;
        },
        cellRendererParams: ({ value }: { value: any }) => value.props,
        menuTabs: ["filterMenuTab"],
        cellRenderer: EmployementStatusRender,
      },
      {
        headerName: "DATE OF JOINING",
        width: 200,
        cellStyle: { paddingTop: "15px", lineHeight: "0" },
        field: "dateofjoining",
        sortable: true,
        filter: "agMultiColumnFilter",
        autoHeight: true,
        wrapText: true,
        comparator(valueA, valueB, ..._other) {
          const valueADate = new Date(valueA.props.dateOfJoin)?.getTime();
          const valueBDate = new Date(valueB.props.dateOfJoin)?.getTime();
          return valueADate - valueBDate;
        },
        filterParams: {
          maxNumConditions: 5,
          buttons: ["reset"],
          filters: [
            {
              title: "Employee Date",
              display: "subMenu",
              filterParams: {
                maxNumConditions: 5,
                inRangeInclusive: true,
                buttons: ["reset"],
                comparator: (
                  filterLocalDateAtMidnight: Date,
                  cellValue: { props: Employee }
                ) => {
                  const filterDate = filterLocalDateAtMidnight;
                  filterDate.setHours(0, 0, 0, 0);
                  const cellDate = new Date(cellValue.props?.dateOfJoin);
                  cellDate.setHours(0, 0, 0, 0);
                  const filterDateAsEpoch = filterDate.valueOf();
                  const cellEpoch = cellDate.valueOf();
                  return cellEpoch - filterDateAsEpoch;
                },
              },
              filter: "agDateColumnFilter",
            },
            {
              title: "Resignation Date",
              display: "subMenu",
              filterParams: {
                maxNumConditions: 5,
                inRangeInclusive: true,
                buttons: ["reset"],
                comparator: (
                  filterLocalDateAtMidnight: Date,
                  cellValue: { props: Employee }
                ) => {
                  const filterDate = filterLocalDateAtMidnight;
                  filterDate.setHours(0, 0, 0, 0);
                  const cellDate = new Date(
                    cellValue.props?.resignationDate || ""
                  );
                  cellDate.setHours(0, 0, 0, 0);
                  const filterDateAsEpoch = filterDate.valueOf();
                  const cellEpoch = cellDate.valueOf();
                  return cellEpoch - filterDateAsEpoch;
                },
              },
              filter: "agDateColumnFilter",
            },
            {
              title: "Last Working Date",
              display: "subMenu",
              filterParams: {
                maxNumConditions: 5,
                inRangeInclusive: true,
                buttons: ["reset"],
                comparator: (
                  filterLocalDateAtMidnight: Date,
                  cellValue: { props: Employee }
                ) => {
                  const filterDate = filterLocalDateAtMidnight;
                  filterDate.setHours(0, 0, 0, 0);
                  const cellDate = new Date(cellValue.props?.lastDay || "");
                  cellDate.setHours(0, 0, 0, 0);
                  const filterDateAsEpoch = filterDate.valueOf();
                  const cellEpoch = cellDate.valueOf();
                  return cellEpoch - filterDateAsEpoch;
                },
              },
              filter: "agDateColumnFilter",
            },
          ],
        },
        menuTabs: ["filterMenuTab"],
        cellRendererParams: ({ value }: { value: any }) => value.props,
        cellRenderer: EmployeeDate,
      },
      {
        headerName: "EMPLOYEE ID",
        width: 200,
        field: "employeeId",
        filter: "agSetColumnFilter",
        filterValueGetter: (params) => params.getValue("employeeId"),
        filterParams: {
          ...filterparams,
          buttons: ["reset"],
          defaultToNothingSelected: true,
          suppressSelectAll: false,
          suppressMiniFilter: false,
          excelMode: "windows",
          comparator: sortComparator,
        },
        menuTabs: ["filterMenuTab"],
        comparator(valueA, valueB, ..._other) {
          return valueA?.localeCompare(valueB);
        },
        sortable: true,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "EMAIL ID",
        width: 200,
        cellStyle: { lineHeight: "20px", paddingTop: "15px" },
        field: "email",
        filter: "agSetColumnFilter",
        filterValueGetter: (params) => params.getValue("email"),
        filterParams: {
          buttons: ["reset"],
          suppressSelectAll: false,
          suppressMiniFilter: false,
          comparator: sortComparator,
        },
        comparator(valueA, valueB, ..._other) {
          return valueA?.localeCompare(valueB);
        },
        sortable: true,
        menuTabs: ["filterMenuTab"],
        suppressSizeToFit: true,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "VESTED FACTOR",
        width: 200,
        cellStyle: { lineHeight: "20px", paddingTop: "15px" },
        field: "vestedFactorData",
        filter: "agMultiColumnFilter",
        comparator: (valueA, valueB, ..._other) =>
          valueA.vestedFactor - valueB.vestedFactor,
        filterParams: {
          maxNumConditions: 5,
          buttons: ["reset"],
          filters: [
            {
              title: "Vested Factor",
              display: "subMenu",
              filter: "agNumberColumnFilter",
              buttons: ["reset"],
              filterValueGetter: (params: any) =>
                params.data.vestedFactorData.vestedFactor,
              filterParams: {
                buttons: ["reset"],
                valueGetter: (params: any) =>
                  params.data.vestedFactorData.vestedFactor,
              },
            },
            {
              title: "Pause Vesting",
              display: "subMenu",
              filter: "agSetColumnFilter",
              buttons: ["reset"],
              filterParams: {
                buttons: ["reset"],
                suppressSelectAll: false,
                suppressMiniFilter: false,
                keyCreator: (params: any) => {
                  const value =
                    params.value.pausedGrants > 0 ? "VESTING PAUSED" : "NONE";
                  return value;
                },
                valueFormatter: (params: any) => {
                  const value =
                    params.value.pausedGrants > 0 ? "VESTING PAUSED" : "NONE";

                  return value;
                },
              },
            },
          ],
        },
        sortable: true,
        menuTabs: ["filterMenuTab"],
        suppressSizeToFit: true,
        autoHeight: true,
        wrapText: true,
        cellRenderer: EmployeeVestedFactor,
      },
      {
        headerName: "NO OF GRANTS",
        width: 200,
        cellStyle: { lineHeight: "20px", paddingTop: "15px" },
        field: "noOfGrants",
        filter: "agNumberColumnFilter",
        filterValueGetter: (params) => params.getValue("noOfGrants"),
        filterParams: {
          comparator: sortComparator,
        },
        sortable: true,
        menuTabs: ["filterMenuTab"],
        suppressSizeToFit: true,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "LOGIN STATUS",
        width: 200,
        cellStyle: { lineHeight: "20px", paddingTop: "15px" },
        field: "lastlogin",
        filter: "agSetColumnFilter",
        sortable: false,
        filterValueGetter: (params) => params.getValue("lastlogin"),
        filterParams: {
          suppressSelectAll: false,
          suppressMiniFilter: false,
          buttons: ["reset"],
        },
        menuTabs: ["filterMenuTab"],
        suppressSizeToFit: true,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "CREDENTIALS GENERATED",
        width: 220,
        cellStyle: { lineHeight: "20px", paddingTop: "15px" },
        field: "generateCredentialData",
        filter: "agMultiColumnFilter",
        filterParams: {
          maxNumConditions: 5,
          buttons: ["reset"],
          filters: [
            {
              title: "Credentials Generated",
              display: "subMenu",
              filter: "agSetColumnFilter",
              buttons: ["reset"],
              filterParams: {
                buttons: ["reset"],
                suppressSelectAll: false,
                suppressMiniFilter: false,
                keyCreator: (params: any) => params.value.status,
                valueFormatter: (params: any) => params.value.status,
              },
            },
            {
              title: "User Email",
              display: "subMenu",
              filter: "agSetColumnFilter",
              buttons: ["reset"],
              filterParams: {
                buttons: ["reset"],
                suppressSelectAll: false,
                suppressMiniFilter: false,
                keyCreator: (params: any) =>
                  params.value.userEmail && params.value.userEmail !== ""
                    ? params.value.userEmail
                    : "(Blanks)",
                valueFormatter: (params: any) =>
                  params.value.userEmail && params.value.userEmail !== ""
                    ? params.value.userEmail
                    : "(Blanks)",
              },
            },
          ],
        },
        sortable: false,
        menuTabs: ["filterMenuTab"],
        suppressSizeToFit: true,
        autoHeight: true,
        wrapText: true,
        cellRenderer: EmployeeCredentialRender,
      },
      {
        headerName: "EMPLOYEE TAGS",
        field: "tags",
        autoHeight: true,
        wrapText: true,
        minWidth: 180,
        filter: "agSetColumnFilter",
        menuTabs: ["filterMenuTab"],
        hide: true,
        sortable: true,
        cellStyle: { paddingTop: "15px", lineHeight: "normal" },
        valueGetter: (params: any) =>
          normalizeEmployeeTags(params.data.tags).map((tag) => tag.label),
        comparator: (valueA: string[] = [], valueB: string[] = []) =>
          (Array.isArray(valueA) ? valueA : [])
            .join(", ")
            .localeCompare((Array.isArray(valueB) ? valueB : []).join(", ")),
        filterParams: {
          suppressSelectAll: false,
          suppressMiniFilter: false,
          buttons: ["reset"],
        },
        cellRenderer: (params: any) => TagsCell({ value: params.data.tags }),
      },
      {
        headerName: "MANAGERS",
        field: "managerNames",
        minWidth: 160,
        filter: "agSetColumnFilter",
        menuTabs: ["filterMenuTab"],
        sortable: true,
        cellStyle: { paddingTop: "15px", lineHeight: "normal" },
        comparator: (valueA: string[] = [], valueB: string[] = []) =>
          (Array.isArray(valueA) ? valueA : [])
            .join(", ")
            .localeCompare((Array.isArray(valueB) ? valueB : []).join(", ")),
        filterParams: {
          suppressSelectAll: false,
          suppressMiniFilter: false,
          buttons: ["reset"],
        },
        cellRenderer: (params: any) =>
          ManagersCell({ value: params.data.managerNames }),
      },
      {
        headerName: "",
        field: "actions",
        pinned: "right",
        hide: false,
        width: 80,
        maxWidth: 80,
        filter: false,
        colId: "action-column",
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        resizable: false,
        sortable: false,
        menuTabs: [],
        cellRendererParams: ({ value }: { value: { props: any } }) =>
          value ? value.props : null,
        cellRenderer: CTADropdown,
      },
    ],
    [isUserAdmin, hasDeletePermission]
  );

  const rowData = useMemo(
    () =>
      employeeTableData?.map((employee) => ({
        avatar: (
          <Avatar name={employee.employeeName} status={employee.employeeName} />
        ),
        employeeInfo: {
          employeeName: employee.employeeName,
          employeeDesignation: employee.employeeDesignation,
          department: employee.department,
          employmentStatus: employee.employmentStatus,
          companyName: employee.companyName,
          grade: employee.grade,
        },
        dateofjoining: (
          <EmployeeDate
            dateOfJoin={employee.dateOfJoin}
            lastDay={employee.lastDay}
            resignationDate={employee.resignationDate}
          />
        ),
        employmentStatus: (
          <EmployementStatusRender
            employmentStatus={
              employee?.isResignationSchedule
                ? "RESIGNATION SCHEDULED"
                : employee.employmentStatus
            }
            isResignationSchedule={employee?.isResignationSchedule ?? false}
            isPendingResignation={(() => {
              if (employee?.isResignationSchedule) return false;
              if (employee.employmentStatus !== EmployementStatus.EMPLOYED)
                return false;
              const now = new Date();
              const resignDate = employee.resignationDate
                ? new Date(employee.resignationDate)
                : null;
              const lastDay = employee.lastDay
                ? new Date(employee.lastDay)
                : null;
              if (resignDate && lastDay)
                return resignDate <= now && lastDay <= now;
              if (
                resignDate &&
                !lastDay &&
                dateForLapsing === "resignationDate"
              )
                return resignDate <= now;
              return false;
            })()}
            dateForLapsing={dateForLapsing}
          />
        ),
        isResignationSchedule: employee.isResignationSchedule,
        employeeId: employee.employeeIdentificationString,
        email: employee.email,
        vestedFactor: employee.totalPercentageVested * 100,
        vestedFactorData: {
          vestedFactor: employee.totalPercentageVested * 100,
          pausedGrants: employee.pausedGrants || 0,
        },
        tags: employee.tags,
        managerNames: (employee.managers || []).map(
          (managerId) => managerUserIdToName.get(managerId) || managerId
        ),
        noOfGrants: employee.noOfGrants,
        lastlogin: employee.lastLogin ? "Yes" : "No",
        generateCredentialData: {
          status: employee.isLoginProvided ? "Yes" : "No",
          userEmail: employee.userEmail || "",
        },
        id: employee.id,
        actions: (
          <CTADropdown
            dropdownClassName=" xl:right-10 lg:right-8 md:right-2"
            actions={[
              {
                name: "Delete Employee",
                disabled:
                  (employee.noOfGrants ?? 0) > 0 ||
                  !!employee.isLoginProvided ||
                  !isUserAdmin ||
                  !hasDeletePermission,
                tooltipText:
                  (employee.noOfGrants ?? 0) > 0
                    ? "Employee has grants and cannot be deleted"
                    : employee.isLoginProvided
                    ? "Revoke credentials before deleting"
                    : !isUserAdmin
                    ? "Admin access required"
                    : !hasDeletePermission
                    ? "Insufficient permissions to delete employee"
                    : undefined,
              },
            ]}
            onAction={(action) => handleAction(action, [employee])}
          />
        ),
      })),
    [employeeTableData, isUserAdmin, hasDeletePermission, managerUserIdToName]
  );
  const onPageSizeChanged = useCallback(() => {
    const value = (document.getElementById("page-size") as HTMLInputElement)
      .value;
    gridApi.current?.api.paginationSetPageSize(Number(value));
  }, []);
  const onGridReady = (params: any) => {
    gridApi.current = params;
    gridRef.current = params.api;
  };

  const [filteredRowData, setFilteredRowData] = useState<any>([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  useEffect(() => {
    const filterModel = gridApi.current?.api.getFilterModel();

    const filteredData = gridApi.current?.api
      .getModel()
      .rowsToDisplay.map((node: any) => node.data);

    setFilteredRowData(filteredData);
    gridApi.current?.api.setFilterModel(tableState?.filterState);

    try {
      const displayed = (gridApi.current?.api.getModel().rowsToDisplay || [])
        .length;
      if (!displayed) gridApi.current?.api.showNoRowsOverlay?.();
      else gridApi.current?.api.hideOverlay?.();
    } catch (e) {
      // ignore
    }
  }, [
    gridApi,
    tableState?.filterState,
    tableState?.columnState,
    employeeTableData,
  ]);

  const onAgGridFilterChanged = (grid: any) => {
    const filtersApplied = grid.api.isAnyFilterPresent();
    const filterModel = grid.api.getFilterModel();

    const isOnlyEmptySetFilters =
      Object.keys(filterModel).length > 0 &&
      Object.values(filterModel).every(
        (filter: any) =>
          filter.filterType === "set" &&
          Array.isArray(filter.values) &&
          filter.values.length === 0
      );

    setIsFilterApplied(filtersApplied);
    tableFilterCount(filtersApplied);

    const filteredData = grid.api
      .getModel()
      .rowsToDisplay.map((node: any) => node.data);
    setFilteredRowData(filteredData);
    uploadFilter(filterModel);
    try {
      const displayed = (grid.api.getModel().rowsToDisplay || []).length;
      if (!displayed) grid.api.showNoRowsOverlay?.();
      else grid.api.hideOverlay?.();
    } catch (e) {
      // ignore
    }
  };

  const [isColumnOpen, setIsColumnOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const openToolPanel = (key: any) => {
    if (key === "columns") {
      if (gridApi) {
        if (!isColumnOpen) gridApi?.current?.api.openToolPanel(key);
        else gridApi?.current?.api.closeToolPanel();
        setIsColumnOpen((state) => !state);
        setIsFilterOpen(false);
      }
    } else if (key === "filters") {
      if (gridApi) {
        if (!isFilterOpen) gridApi?.current?.api.openToolPanel(key);
        else gridApi?.current?.api.closeToolPanel();
        setIsFilterOpen((state) => !state);
        setIsColumnOpen(false);
      }
    }
  };

  return (
    <VStack className="w-full">
      <HStack className="items-center justify-between w-full gap-4 py-4">
        <HStack className="items-center gap-4 px-6">
          <SearchInput
            className=""
            placeholder="Search"
            onChange={(e: any) => {
              gridApi.current.api.setQuickFilter(e.target.value);
            }}
          />
        </HStack>

        <HStack className="items-center justify-end gap-4">
          <CloudSetting
            tableId={TableId.employeeTable}
            isLoading={isSaving}
            filterCondition={filterCondition}
            setFilterCondition={setFilterCondition}
            gridApi={gridApi}
          />
          <IconCTAButton
            value={"Columns"}
            onClick={() => openToolPanel("columns")}
            iconName={"fluent:column-triple-edit-20-regular"}
            className={`px-4 font-medium items-center flex flex-row ${
              isColumnOpen ? "text-orange-501" : "text-gray-400"
            }`}
            selected={isColumnOpen}
          />
          <IconCTAButton
            value={"Filters"}
            onClick={() => openToolPanel("filters")}
            iconName={"material-symbols:filter-alt"}
            className={`px-4 font-medium items-center flex flex-row ${
              isFilterOpen ? "text-orange-501" : "text-gray-400"
            }`}
            selected={isFilterOpen}
          />
          {gridApi?.current && (
            <AdvancedAgGridSearch
              gridApi={gridApi?.current?.api}
              tableIdentifier="employeeTable"
            />
          )}
        </HStack>
      </HStack>
      <HStack className="justify-between w-full">
        <Box
          style={{
            height: `${
              (rowData.length >= 10 ? 10 : rowData.length + 3) * 60
            }px`,
          }}
          className="w-full h-full max-h-full overflow-x-auto bg-black ag-theme-material"
        >
          <AgGridReact
            onFilterChanged={onAgGridFilterChanged}
            onColumnEverythingChanged={uploadColumn}
            onColumnMoved={uploadColumn}
            onColumnVisible={uploadColumn}
            onColumnPinned={uploadColumn}
            onColumnResized={uploadColumn}
            alwaysShowHorizontalScroll
            rowClass={
              "border-t border-dashed cursor-pointer hover:bg-slate-50 "
            }
            alwaysMultiSort
            onGridReady={onGridReady}
            sideBar={sideBar}
            animateRows={true}
            defaultColDef={defaultColDef}
            rowData={rowData}
            suppressCopyRowsToClipboard={true}
            suppressCopySingleCellRanges={true}
            suppressCellFocus={true}
            components={componentsRegistery}
            suppressMenuHide={true}
            columnDefs={columnDefs}
            onCellClicked={handleCellClick}
            rowMultiSelectWithClick={true}
            rowSelection="multiple"
            onSelectionChanged={handleSelectionChanged}
            suppressRowClickSelection={true}
            pagination={true}
            overlayNoRowsTemplate={
              '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;   margin-top: 50px;">No Rows To Show</span>'
            }
          ></AgGridReact>
        </Box>
      </HStack>
    </VStack>
  );
}

function EmployeeInfoRender(emp: any) {
  return (
    <HStack>
      <Avatar
        name={emp.value.employeeName}
        status={emp.value.employmentStatus}
      />
      <Box className="ml-2">
        <p className="text-xs font-medium text-gray-dark">
          {emp.value.employeeName}
        </p>
        <p className="capitalize text-xxs text-gray-light">
          {[
            emp.value.employeeDesignation,
            emp.value.department,
            emp.value.grade,
          ]
            .filter((v) => v && v !== null && v !== "")
            .map((v) => v.toLowerCase())
            .join(" | ") || " "}
        </p>
        {emp.value.companyName && (
          <p className="text-xxs text-gray-light">{emp.value.companyName}</p>
        )}
      </Box>
    </HStack>
  );
}
function EmployementStatusRender(emp: {
  employmentStatus: string;
  isResignationSchedule: boolean;
  isPendingResignation?: boolean;
  dateForLapsing?: string;
}) {
  const statuses = [
    "EMPLOYED",
    "RESIGNED",
    "PENDING",
    "RESIGNATION SCHEDULED",
    "PENDING RESIGNATION",
  ];
  const status = emp.isPendingResignation
    ? "PENDING RESIGNATION"
    : emp.isResignationSchedule
    ? "RESIGNATION SCHEDULED"
    : (emp.employmentStatus?.toUpperCase() as
        | "EMPLOYED"
        | "RESIGNED"
        | "PENDING"
        | "RESIGNATION SCHEDULED"
        | "PENDING RESIGNATION");
  if (!statuses.includes(status)) return <></>;
  const { color, backgroundColor } = StatusLabelColors[status];
  const tooltipText =
    status === "RESIGNATION SCHEDULED"
      ? "Resignation has been initiated. Last working day is yet to arrive."
      : status === "PENDING RESIGNATION"
      ? emp.dateForLapsing === "resignationDate"
        ? "Resignation has been initiated. Resignation date has already passed."
        : "Resignation has been initiated. Last working day has already passed."
      : undefined;
  const badge = (
    <span
      className={`flex items-center capitalize min-w-10 h-6  rounded-sm font-medium text-center text-[9px]  leading-4 px-1`}
      style={{ color, backgroundColor }}
    >
      {emp.isPendingResignation
        ? "PENDING RESIGNATION"
        : emp.isResignationSchedule
        ? "RESIGNATION SCHEDULED"
        : status}
    </span>
  );
  return (
    <HStack>
      <Box className="min-h-4 ">
        {tooltipText ? (
          <MuiTooltip title={tooltipText} arrow placement="top">
            <span>{badge}</span>
          </MuiTooltip>
        ) : (
          badge
        )}
      </Box>
    </HStack>
  );
}

function EmployeeDate(emp: {
  dateOfJoin?: string;
  lastDay?: string;
  resignationDate?: string;
}) {
  const formattedDate = new Date(emp.dateOfJoin || " ")
    .toUTCString()
    .slice(5, 17);
  const formattedLastDay = new Date(emp.lastDay || "")
    .toUTCString()
    .slice(5, 17);
  const formattedResignationDate = new Date(emp.resignationDate || "")
    .toUTCString()
    .slice(5, 17);
  return (
    <Box className="">
      <HStack>
        <Box>
          <p className="text-xs font-medium text-gray-dark">{formattedDate}</p>
          {emp.resignationDate && (
            <p className="capitalize text-xxs text-gray-light">{`Resignation Date: ${formattedResignationDate}`}</p>
          )}
          {emp.lastDay && (
            <p className="capitalize text-xxs text-gray-light">{`Last Day: ${formattedLastDay}`}</p>
          )}
        </Box>
      </HStack>
    </Box>
  );
}

function EmployeeCredentialRender(data: any) {
  const { status, userEmail } = data.value || {};
  return (
    <Box className="">
      <HStack>
        <Box>
          <p className="text-xs font-medium text-gray-dark">{status}</p>
          {status === "Yes" && userEmail && (
            <p className="text-xxs text-gray-light">{userEmail}</p>
          )}
        </Box>
      </HStack>
    </Box>
  );
}

function EmployeeVestedFactor(data: any) {
  const value =
    data.value.vestedFactor != null
      ? `${parseFloat(data.value.vestedFactor.toFixed(2))}%`
      : "0%";
  const { color, backgroundColor } = StatusLabelColors.PENDING;
  return (
    <HStack>
      <VStack className="ml-2">
        <p className="text-xs font-medium text-gray-dark">{value}</p>
        {(data.value.pausedGrants || 0) > 0 && (
          <span
            className={`flex items-center capitalize min-w-10 h-6  rounded-sm font-medium text-center text-[9px]  leading-4 px-1 whitespace-nowrap mt-1 mb-1`}
            style={{ color, backgroundColor }}
          >
            VESTING PAUSED
          </span>
        )}
      </VStack>
    </HStack>
  );
}

export default EmployeesTableAgGrid;
